/** Only ever viewed, never started. It offers several create flows side by side, so it is not tied to a session. */
export type SkillViewFlowTouchPoint =
	| 'chatImportSkillButton'
	| 'chatSeeAllSkillsModal'
	| 'studioBrowseSkillsModal'
	| 'chatFullScreenSkillsModal';

export type SkillCreateFlowTouchPoint =
	| 'manualCreationLinks'
	| 'studioUniversalCreate'
	| 'studioCustomSkillsPage'
	| 'chatSkillListBuildSkill'
	| 'chatAutoSuggestBuildSkill'
	| 'chatTypeAheadBuildSkill'
	| 'chatCreateSkillWithChat'
	| 'chatCreateSkillWithEditor'
	| 'chatSeeAllCreateSkillWithChat'
	| 'chatSeeAllCreateSkillWithEditor'
	| 'studioBrowseCreateSkillWithChat'
	| 'studioBrowseCreateSkillWithEditor'
	| 'chatFullScreenCreateSkillWithChat'
	| 'chatFullScreenCreateSkillWithEditor'
	/** The chat drafted a skill on its own, rather than from a create-skill entry point. */
	| 'chatImplicitCustomSkill';

export type SkillUpdateFlowTouchPoint =
	| 'studioCustomSkillDetailsPage'
	| 'studioCustomSkillListUpdateAction'
	| 'chatSkillsListEditAction'
	| 'chatSkillCardRenderer';

export type SkillDeleteFlowTouchPoint =
	| 'agentStudioSkillListDeleteAction'
	| 'studioCustomSkillListDeleteAction'
	| 'chatSkillsListDeleteAction';

/** Why a skill create, update or delete did not go through. */
export type SkillFailureReason =
	| 'mutationErrors'
	| 'unsuccessfulResponse'
	| 'requestError'
	| 'duplicateName';

export type SkillFlowTouchPoint =
	| SkillCreateFlowTouchPoint
	| SkillUpdateFlowTouchPoint
	| SkillDeleteFlowTouchPoint;

// Possible session flows for custom skills
export type SessionFlowSlot =
	| 'chatSkill' // Create or update skill from chat
	| 'createModal' // Create skill from modal
	| 'createPage' // Create skill from page
	| 'updateModal' // update skill from modal
	| 'updatePage' // update skill from page
	| 'deleteDialog'; // dialog to delete skill

// Each flow has its own session. So we are able to have multiple sessions at the same time
export const TOUCH_POINT_SESSION_SLOT: Record<SkillFlowTouchPoint, SessionFlowSlot> = {
	chatCreateSkillWithEditor: 'createModal',
	chatSeeAllCreateSkillWithEditor: 'createModal',
	studioBrowseCreateSkillWithEditor: 'createModal',
	chatFullScreenCreateSkillWithEditor: 'createModal',
	studioCustomSkillsPage: 'createPage',
	manualCreationLinks: 'createPage',
	studioUniversalCreate: 'createPage',
	chatCreateSkillWithChat: 'chatSkill',
	chatSeeAllCreateSkillWithChat: 'chatSkill',
	studioBrowseCreateSkillWithChat: 'chatSkill',
	chatFullScreenCreateSkillWithChat: 'chatSkill',
	chatAutoSuggestBuildSkill: 'chatSkill',
	chatTypeAheadBuildSkill: 'chatSkill',
	chatSkillListBuildSkill: 'chatSkill',
	chatImplicitCustomSkill: 'chatSkill',
	studioCustomSkillListUpdateAction: 'updatePage',
	studioCustomSkillDetailsPage: 'updatePage',
	chatSkillCardRenderer: 'updateModal',
	chatSkillsListEditAction: 'updateModal',
	studioCustomSkillListDeleteAction: 'deleteDialog',
	chatSkillsListDeleteAction: 'deleteDialog',
	agentStudioSkillListDeleteAction: 'deleteDialog',
};

/** How far the flow has progressed, named after the event that got it there. */
export type SkillCreateFlowStage = 'startedSkillFlow' | 'draftCardViewed';

export type SkillCreateFlowSessionState = {
	/** Joins every event of a single pass through the flow. */
	traceId: string;
	touchPoint: SkillFlowTouchPoint;
	stage: SkillCreateFlowStage;
};

export type FireSkillEventAttributes = {
	touchPoint?: SkillFlowTouchPoint | SkillViewFlowTouchPoint;
	traceId?: string;
	isUpdate?: boolean;
	failureReason?: SkillFailureReason;
};

export type SkillFlowSessionHandle = {
	readonly slot: SessionFlowSlot;
	readonly state: SkillCreateFlowSessionState | undefined;
	advanceTo: (stage: SkillCreateFlowStage) => SkillFlowSessionHandle;
	clear: () => void;
};

// What is the source of the skill creation
export type SkillFlowSource = 'chat' | 'modal' | 'page';

export const CREATE_SOURCE_SESSION_SLOT: Record<SkillFlowSource, SessionFlowSlot> = {
	chat: 'chatSkill',
	modal: 'createModal',
	page: 'createPage',
};

export const UPDATE_SOURCE_SESSION_SLOT: Record<SkillFlowSource, SessionFlowSlot> = {
	chat: 'chatSkill',
	modal: 'updateModal',
	page: 'updatePage',
};
